/**
 * MemberPortalPage — /home-os/family/:memberId
 *
 * One person. One member.id. One underlying record. Multiple views.
 * Every tab is a filtered view into the central HomeOSState — nothing is
 * copied. Money is owner/partner-restricted in Phase 1 (no member ACL yet).
 */
import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { usePersistentState } from "./state/usePersistentState";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  TextLink, PillButton, Caption, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";
import { FamilyMemberEditModal } from "./components/FamilyMemberEditModal";
import { migrateMemberToV2, identityLine, LIVING_TEXT_FIELDS } from "./utils/memberIntelligence";
import { getUpcomingFamilyBirthdays, birthdayRelative } from "./utils/birthdayIntelligence";
import {
  PortalToday, PortalCalendar, PortalGoals, PortalPlanning, PortalMoney, PortalChores,
} from "./components/MemberPortalSections";

const TABS = [
  ["today", "Today"], ["calendar", "Calendar"], ["profile", "Profile"], ["goals", "Goals"],
  ["planning", "Planning"], ["money", "Money"], ["chores", "Chores"],
];

const localISO = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function MemberPortalPage() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { household: authHousehold } = useAuth();
  const state = useHomeOSState();
  const { members, tasks, goals, dailyFamilySchedule, updateTask, addGoal, updateGoal, removeGoal } = state;
  const [moneyGoals] = usePersistentState("money.goals", []);
  const { phase } = useLightPhase();

  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "today";
  const setTab = (t) => setParams({ tab: t }, { replace: true });

  const [editingProfile, setEditingProfile] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [goalDraft, setGoalDraft] = useState({ title: "", progress: "", status: "not_started" });

  const raw = (members || []).find(m => m.id === memberId);
  const member = useMemo(() => (raw ? migrateMemberToV2(raw) : null), [raw]);
  const firstName = ((member?.preferredName || member?.name || "").trim().split(" ")[0]) || "Member";
  const role = authHousehold?.role || "viewer";
  const moneyAllowed = role === "owner" || role === "partner";

  const todayISO = localISO();

  /* Their schedule — personal events + whole-family events. */
  const memberEvents = useMemo(() =>
    (dailyFamilySchedule || [])
      .filter(e => e.status !== "done" && e.startTime && (e.memberId === memberId || e.memberId === "all" || e.memberId === "everyone"))
      .map(e => ({ ...e, shared: e.memberId === "all" || e.memberId === "everyone" }))
      .sort((a, b) => (a.startTime || "").localeCompare(b.startTime || "")),
    [dailyFamilySchedule, memberId]);

  /* Their open tasks — central records only. */
  const memberTasks = useMemo(() =>
    (tasks || []).filter(t =>
      (t.assignedTo === memberId || t.assignedTo === member?.name) &&
      !["Done", "Completed", "Ara suggested", "Paused"].includes(t.status)),
    [tasks, memberId, member?.name]);

  const withDueLabel = (t) => ({
    ...t,
    dueLabel: t.dueDate
      ? (t.dueDate < todayISO ? "gently overdue" : t.dueDate === todayISO ? "due today" : null)
      : null,
  });

  const openToday = useMemo(() =>
    memberTasks.filter(t => !t.dueDate || t.dueDate <= todayISO).map(withDueLabel),
    [memberTasks, todayISO]);

  const completeTask = (t) => {
    updateTask(t.id, { status: "Done" });
    toast.success(t.category === "Chores"
      ? `Thanks, ${firstName} — you've contributed to the family today.`
      : "Done — taken off the list.");
  };

  /* Calendar week — dated central tasks for this member, next 7 days. */
  const week = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(); d.setDate(d.getDate() + i);
      const iso = localISO(d);
      days.push({
        iso,
        label: i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "short" }),
        items: memberTasks.filter(t => t.dueDate === iso),
      });
    }
    return days;
  }, [memberTasks, todayISO]);

  const birthdayLine = useMemo(() => {
    if (!member) return null;
    const b = getUpcomingFamilyBirthdays([member]).find(x => x.daysUntil <= 30);
    return b ? `${firstName}'s birthday is ${birthdayRelative(b.daysUntil).toLowerCase()}.` : null;
  }, [member, firstName]);

  /* Goals — central state.goals, same records edited in place. */
  const memberGoals = useMemo(() =>
    (goals || []).filter(g => g.ownerType === "member" && g.ownerMemberId === memberId),
    [goals, memberId]);

  const startGoalEdit = (g) => {
    setEditingGoalId(g.id);
    setGoalDraft({ title: g.title || "", progress: g.progress || "", status: g.status || "not_started" });
  };
  const saveGoal = () => {
    updateGoal(editingGoalId, { ...goalDraft });
    setEditingGoalId(null);
    toast.success("Goal updated.");
  };
  const addMemberGoal = () => {
    const id = addGoal({ title: "New goal", ownerType: "member", ownerMemberId: memberId, status: "not_started" });
    startGoalEdit({ id, title: "New goal", progress: "", status: "not_started" });
  };
  const removeMemberGoal = (id) => { removeGoal(id); setEditingGoalId(null); toast.success("Goal removed."); };

  /* Planning — dated commitments (14 days) + profile rhythm. */
  const upcoming = useMemo(() =>
    memberTasks
      .filter(t => t.dueDate && t.dueDate >= todayISO)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
      .slice(0, 10)
      .map(t => ({
        ...t,
        whenLabel: t.dueDate === todayISO ? "Today"
          : new Date(t.dueDate + "T00:00:00").toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" }),
      })),
    [memberTasks, todayISO]);

  const rhythmRows = useMemo(() => {
    if (!member) return [];
    return LIVING_TEXT_FIELDS
      .map(([key, label]) => [label, member.living?.[key]])
      .filter(([, v]) => v && String(v).trim());
  }, [member]);

  /* Money — exact-name association only; no free-text guessing beyond equality. */
  const savings = useMemo(() => {
    if (!member) return [];
    const names = [member.name, member.preferredName].filter(Boolean).map(n => n.trim().toLowerCase());
    return (moneyGoals || []).filter(g => names.includes((g.owner || "").trim().toLowerCase()));
  }, [moneyGoals, member]);

  /* Chores — weekly chart from central tasks. */
  const choreGroups = useMemo(() => {
    const chores = memberTasks.filter(t => t.category === "Chores").map(withDueLabel);
    const inWeek = (t) => t.dueDate && t.dueDate > todayISO && week.some(d => d.iso === t.dueDate);
    return [
      { key: "overdue", label: "Gently overdue", chores: chores.filter(t => t.dueDate && t.dueDate < todayISO) },
      { key: "today", label: "Today", chores: chores.filter(t => t.dueDate === todayISO) },
      { key: "week", label: "Later this week", chores: chores.filter(inWeek) },
      { key: "anytime", label: "Anytime", chores: chores.filter(t => !t.dueDate) },
    ];
  }, [memberTasks, todayISO, week]);

  /* ── Not found ─────────────────────────────────────────────────────── */
  if (state.hydrated && !member) {
    return (
      <ResidenceLayout rail={<ResidenceRail currentKey="family" />} phase={phase} testid="member-portal-missing">
        <div className="pt-16">
          <p className="italic text-[19px]" style={{ fontFamily: "var(--ara-font-display), serif", color: "var(--ara-ink)" }}>
            No one lives here by that name.
          </p>
          <div className="mt-5">
            <TextLink onClick={() => navigate("/home-os/family")} testid="portal-back-to-family">← Back to the Family Room</TextLink>
          </div>
        </div>
      </ResidenceLayout>
    );
  }
  if (!member) return null;

  const outstanding = openToday.length;

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="family" />} phase={phase} testid="member-portal">
      <PageHeading
        testid="member-portal-hero"
        module="family"
        art="family"
        room="The Family Room"
        title={`${firstName}'s Space`}
        focal={identityLine(member) || "A member of this household."}
        actions={
          <>
            <PillButton variant="solid" onClick={() => setEditingProfile(true)} testid="portal-edit-profile-btn">
              Edit profile
            </PillButton>
            <TextLink onClick={() => navigate("/home-os/family")} testid="portal-back-link">← Family Room</TextLink>
          </>
        }
      />

      {/* Quiet tab rail */}
      <nav className="flex flex-wrap gap-x-7 gap-y-2 pb-4 border-b" style={{ borderColor: "var(--ara-line-soft)" }} data-testid="member-portal-tabs">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`member-portal-tab-${key}`}
            className="text-[12px] tracking-[0.14em] uppercase transition-ara pb-1"
            style={{
              fontFamily: "Inter, system-ui, sans-serif",
              color: tab === key ? "var(--ara-ink)" : "var(--ara-ink-soft)",
              fontWeight: tab === key ? 700 : 500,
              borderBottom: tab === key ? "1px solid var(--ara-ink)" : "1px solid transparent",
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="pt-8 pb-4" data-testid={`member-portal-panel-${tab}`}>
        {tab === "today" && (
          <PortalToday
            events={memberEvents}
            openTasks={openToday}
            worthKnowing={member.araShouldKnow?.[0] || null}
            onComplete={completeTask}
          />
        )}
        {tab === "calendar" && (
          <PortalCalendar events={memberEvents} week={week} birthdayLine={birthdayLine} />
        )}
        {tab === "profile" && (
          <div data-testid="portal-profile">
            <Caption>The profile</Caption>
            <div className="mt-2 mb-6" data-testid="portal-profile-summary">
              {[
                ["Name", member.name],
                ["Preferred name", member.preferredName],
                ["Pronouns", member.pronouns],
                ["Life stage", member.lifeStage],
                ["Birthday", member.birthday],
                ["Relationship", (member.relationships || []).join(", ")],
              ].filter(([, v]) => v && String(v).trim()).map(([label, value]) => (
                <div key={label} className="py-2.5 border-b last:border-b-0 grid grid-cols-[140px_1fr] gap-4" style={{ borderColor: "var(--ara-line-soft)" }}>
                  <p className="caption-ara" style={{ color: "var(--ara-ink-soft)" }}>{label}</p>
                  <p className="text-[14px]" style={{ color: "var(--ara-ink)", fontFamily: "Inter, system-ui, sans-serif", fontWeight: 500 }}>{value}</p>
                </div>
              ))}
            </div>
            <PillButton variant="solid" onClick={() => setEditingProfile(true)} testid="portal-profile-edit-btn">
              Edit the full profile
            </PillButton>
            <p className="mt-4 caption-ara" style={{ color: "var(--ara-ink-soft)" }}>
              Food, care, living rhythm, celebrations and everything ARA should know live in the full profile.
            </p>
          </div>
        )}
        {tab === "goals" && (
          <PortalGoals
            goals={memberGoals}
            editingId={editingGoalId}
            draft={goalDraft}
            setDraft={setGoalDraft}
            onStartEdit={startGoalEdit}
            onSave={saveGoal}
            onCancel={() => setEditingGoalId(null)}
            onAdd={addMemberGoal}
            onRemove={removeMemberGoal}
          />
        )}
        {tab === "planning" && (
          <PortalPlanning
            upcoming={upcoming}
            responsibilities={member.responsibilities || []}
            rhythmRows={rhythmRows}
            onEditProfile={() => setEditingProfile(true)}
          />
        )}
        {tab === "money" && (
          <PortalMoney allowed={moneyAllowed} savings={savings} onOpenStudy={() => navigate("/home-os/money")} />
        )}
        {tab === "chores" && (
          <PortalChores groups={choreGroups} onComplete={completeTask} />
        )}
      </div>

      <Colophon testid="member-portal-colophon">
        ARA · {firstName}'s Space · {outstanding === 0 ? "all clear" : `${outstanding} open action${outstanding === 1 ? "" : "s"}`}
      </Colophon>

      <FamilyMemberEditModal
        open={editingProfile}
        memberId={member.id}
        mode="edit"
        onClose={() => setEditingProfile(false)}
      />
    </ResidenceLayout>
  );
}
